import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

import DataGrid from 'app/components/DataGrid';
import classes from 'app/components/DataGrid/classes.module.scss';
import useCustomers from 'app/hooks/useCustomers';
import useDeeplink from 'app/hooks/useDeeplink';
import CustomerModal from './CustomerModal';
import MomentsCell from 'app/components/DataGrid/MomentsCell';
import CustomerUploadProgressBar from './CustomerUploadProgressBar';

import {
  getCustomer,
  getCustomers as exportCustomersSvc
} from 'app/services/customers';

import {
  getSimpleJobs
} from 'app/services/jobs';

import {
  getKiosks
} from 'app/services/kiosks';

import {
  setPhotoSwipeOpen,
  setPhotoSwipeMoment
} from 'app/actions/photoSwipeActions';

import {
  AND_OPERATOR,
} from 'app/hooks/useFilter';

import {
  moments as momentsPath
} from 'app/routes';

import {
  formatJobSelectLabel,
  formatJobOptionLabel,
  formatJobCellValue,
  //formatJobOptions
} from 'app/utils/valueFormatters';

import {
  createOption,
  formatOptions
} from 'app/utils/options';

import {
  moments
} from 'app/resources';

import {
  getLocalizedDateTime
} from 'shared/utils/Utils';

const Customers = () => {
  const account = useSelector(state => state.portalReducer.account);
  const user = useSelector(state => state.userReducer.user);

  const [kioskOptions, setKioskOptions] = useState([]);
  const loadKioskOptions = async () => {
    let kiosks = await getKiosks(account.id);
    setKioskOptions(formatOptions(kiosks));
  }
  useEffect(() => {
    if (account?.feature_flags.kiosk_mode) {
      loadKioskOptions();
    }
  }, []);

  const {
    resource,
    formIsOpen,
    currentRecord,
    onFormClose,
    onAdd,
    onEdit,
    onQuickAdd,
    onQuickEdit,
    onDataGridLoad,
    onFormSubmit,
    onDestroy,
    showFilters,
    getRowData,
    updateRow,
    getFilterModel
  } = useCustomers();

  const exportCSV = () => {
    let params = {
      page: 0,
      per_page: 1000,
      is_export: true
    }

    const filterModel = getFilterModel();
    if (filterModel) {
      params.filter_model = filterModel;
    }

    exportCustomersSvc(params).then((resp => resp.blob())).then((blob) => {
      const elem = window.document.createElement('a');
      elem.href = window.URL.createObjectURL(blob);
      elem.download = `customers_${new Date().toISOString()}.csv`;
      document.body.appendChild(elem);
      elem.click();
      document.body.removeChild(elem);
    })
  }

  const deeplink = useDeeplink();

  const showAdd = !account.feature_flags.disable_add_customer;
  const showClubMed = account.feature_flags.club_med;
  const showHawks = account.feature_flags.hawks;
  const showKiosk = account.feature_flags.kiosk_mode;
  const showAccessCode = !account.feature_flags.hide_access_code;

  const getJobOptions = async (page = 1, perPage = 100, filter = null) => {
    let jobs = await getSimpleJobs();
    return formatOptions(jobs);
  }

  // todo: put in shared spot
  const statusOptions = [
    createOption('Created', 0),
    createOption('Preparing Invite', 1),
    createOption('Invite Sent', 2),
    createOption('Viewed', 3),
    createOption('Invite Failed', 4)
  ]

  const viewMomentsForCustomer = async storyId => {
    // filter moments by customer ahead of showing the moments table.
    // this will overwrite any filters previously set.
    // filter moments by customer ahead of showing the moments table.
    // this will overwrite any filters previously set.

    let filterModel = {
      story_id: [{
        filterClass: 'text',
        filterType: 'equals',
        value: storyId.toString(),
        operator: AND_OPERATOR
      }]
    }

    deeplink.load(moments, filterModel);
  }

  const header = 'Customers';
  const addButtonLabel = 'Add Customer';

  let columnOrder;
  if (showClubMed) {
    columnOrder = [
      'moments',
      'upload_status',
      'name',
      'email',
      'room',
      'check_in',
      'check_out',
      'kiosk_id'
    ]
  } else if (showHawks) {
    columnOrder = [
      'moments',
      'name',
      'email',
      'phone',
      'job_name',
      'section',
      'row',
      'seat'
    ]
  } else {
    columnOrder = [
      'moments',
      'upload_status',
      'name',
      'email',
      'phone',
      'access_code',
      'job_name',
      'kiosk_id',
      'status',
      'notify_count'
    ]
  }

  let visibleColumns = [].concat(columnOrder);

  // if kiosk mode then show kiosk column,
  // otherwise show job column
  if (showKiosk) {
    visibleColumns = visibleColumns.filter(col => col !== 'job_name');
  } else {
    visibleColumns = visibleColumns.filter(col => col !== 'kiosk_id');
  }

  let omitColumns = ['job'];
  if (!showAccessCode) {
    omitColumns.push('access_code');
  }

  const onMomentClick = moment => {
    dispatch(setPhotoSwipeMoment(moment));
    dispatch(setPhotoSwipeOpen(true));
  }

  const setPhotoSwipeItem = customer => {
    if (customer.cover_moment) {
      return {
        id: customer.cover_moment.id,
        src: customer.cover_moment.preview_url,
        w: customer.cover_moment.width,
        h: customer.cover_moment.height,
        digital_edit: customer.cover_moment.digital_edit,
        orig_moment_id: customer.cover_moment.orig_moment_id,
      }
    }
  };

  // setup callbacks for uploads via both modal and grid cell
  const onUploadStart = (upload, { customer }) => {
    customer.last_upload = upload;
    updateRow(customer);
  }

  const onUploadComplete = async (upload, { customer }) => {
    let updatedCustomer = await getCustomer(customer.id);
    updatedCustomer.last_upload = upload;
    updateRow(updatedCustomer);
  }

  const columnDefOverrides = [
    {
      field: 'moments',
      headerName: 'Moments',
      pinned: 'left',
      inputType: 'number',
      width: 11 * 16,
      thumbnail: data => data && data.cover_moment && data.cover_moment.thumb_url,
      onThumbnailClick: data => onMomentClick(data.cover_moment),
      cellRenderer: params => {
        const {
          data,
          colDef
        } = params;

        if (data) {
          const {
            id,
            photo_count,
            job_id,
            story_id
          } = data;

          const onImagesClick = () => viewMomentsForCustomer(story_id);

          // disabling the upload label for now,
          // as uploads from here are disabled
          let showUploadLabel = false;
          // only show UPLOAD label if no images
          //let showUploadLabel = !photo_count;

          return (
            <MomentsCell
              data={data}
              colDef={colDef}
              showImageCount={true}
              imageCount={photo_count}
              onImagesClick={onImagesClick}
              showUpload={false}
              showUploadLabel={showUploadLabel}
              uploadJobId={job_id}
              uploadCustomerId={id}
              uploadStoryIds={[story_id]}
              onUploadStart={onUploadStart}
              onUploadComplete={onUploadComplete}
              customer={data}
            />
          );
        }
        return '';
      }
    },
    {
      field: 'upload_status',
      headerName: 'Last Upload / Status',
      //pinned: 'left',
      excludeFromMaxWidth: true,
      cellRenderer: params => {
        let { data } = params;
        let upload = data?.last_upload;
        if (upload) {
          if (upload.completed_at) {
            let lastUpload = getLocalizedDateTime(user.locale, upload.completed_at);
            return <span className={classes.gridCell}>{lastUpload}</span>;
          } else {
            return <CustomerUploadProgressBar customer={data} />;
          }
        }
        return '';
      }
    },
    {
      field: 'job_name',
      headerName: 'Job',
      excludeFromMaxWidth: true,
      renderValues: data => data.job && [data.job.name, data.job.event_id],
      dataValueFormatter: (data, value) => {
        return formatJobCellValue(data.job);
      },
      quickEdit: true,
      inputType: 'autocomplete',
      getInputOptions: getJobOptions,
      selectedOptionFormatter: data => formatOptions([data.job])[0]
    },
    {
      field: 'kiosk_id',
      headerName: 'Kiosk',
      minWidth: 250,
      dataValueFormatter: (data, value) => {
        return formatJobCellValue(data.kiosk);
      },
      quickEdit: true,
      inputType: 'select',
      inputOptions: kioskOptions
    },
    { field: 'name', quickEdit: true },
    { field: 'email', quickEdit: true },
    { field: 'phone', quickEdit: true },
    { field: 'access_code', quickEdit: true },
    {
      field: 'notify_count',
      headerName: 'Invites Sent',
      inputType: 'number'
    },
    {
      field: 'status',
      inputType: 'select',
      inputOptions: statusOptions,
      cellRenderer: params => {
        let data = params.data;
        if (data) {
          const { status_id } = data;
          let status = statusOptions.find(opt => opt.value == status_id);
          status &&= status.label;

          return (
            <div className={classes.gridCell}>
              {status}
            </div>
          );
        }
        return null;
      }
    },
    { field: 'id', minWidth: 10 * 16 }
  ]

  const newRowTemplate = {
    // removed jobs from Redux, will need to figure this out
    job_id: null //jobs && jobs[0] && jobs[0].id
  }

  const onNotify = records => {

  }

  const actions = [
    { key: 'notify', label: 'Notify Customers', callback: onNotify },
    { key: 'destroy', label: 'Delete Customers', callback: onDestroy },
  ]

  const buttons = [
    { key: 'export', icon: <FileDownloadIcon />, label: 'CSV', onClick: exportCSV }
  ]

  return (
    <div>
      <DataGrid
        resource={resource}
        header={header}
        getRowData={getRowData}
        addButtonLabel={addButtonLabel}
        showAdd={showAdd}
        onAdd={onAdd}
        showSelect={true}
        showEdit={true}
        onEdit={onEdit}
        quickEditEnabled={true}
        onQuickAdd={onQuickAdd}
        onQuickEdit={onQuickEdit}
        newRowTemplate={newRowTemplate}
        columnOrder={columnOrder}
        visibleColumns={visibleColumns}
        omitColumns={omitColumns}
        columnDefOverrides={columnDefOverrides}
        setPhotoSwipeItem={setPhotoSwipeItem}
        showFilters={showFilters}
        actions={actions}
        loadingMessage="Loading customers..."
        onDataGridLoad={onDataGridLoad}
        buttons={buttons}
      />
      <CustomerModal
        isOpen={formIsOpen}
        onClose={onFormClose}
        customer={currentRecord}
        onFormSubmit={onFormSubmit}
        onDestroy={onDestroy}
        onUploadStart={onUploadStart}
        onUploadComplete={onUploadComplete}
        kioskOptions={kioskOptions}
      />
    </div>
  );
}

export default Customers;
